import { FC, useCallback, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Add, ArrowBack, Delete, Edit, Star } from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  TableContainer,
  Tooltip,
  Typography,
} from '@mui/material'

import GRCircularProgress from '../../../components/GRCircularProgress/GRCircularProgress'
import { GRTable, GRTableColumn } from '../../../components/GRTable/GRTable'
import { displaySnackBar } from '../../../features/snackbar'
import { useAppDispatch } from '../../../hooks/storeHooks'
import usePage from '../../../hooks/usePage'
import { useRequiredParams } from '../../../hooks/useRequiredParams'
import pageService from '../../../services/PageService'
import {
  useCreateImageVersionMutation,
  useCreateTextVersionMutation,
  useDeleteImageVersionMutation,
  useDeleteTextVersionMutation,
  useGetImageDefinitionQuery,
  useGetImageVersionsQuery,
  useGetTextDefinitionQuery,
  useGetTextVersionsQuery,
  useUpdateImageDefinitionMutation,
  useUpdateImageVersionMutation,
  useUpdateTextDefinitionMutation,
  useUpdateTextVersionMutation,
} from '../../api/ai-prompts'
import { VersionFormDialog, VersionFormValues } from '../../features/ai-prompts/components/VersionFormDialog/VersionFormDialog'
import {
  AiPromptDefinitionResponseDTO,
  AiPromptVersion,
  AiTextPromptVersionResponseDTO,
  isTextVersion,
  PromptType,
} from '../../features/ai-prompts/types/types'

type VersionTableCustomProps = {
  definition?: AiPromptDefinitionResponseDTO
  isText: boolean
  onEdit: (version: AiPromptVersion) => void
  onSetActive: (version: number) => void
  onDelete: (version: number) => void
}

const buildVersionColumns = (): GRTableColumn<AiPromptVersion, VersionTableCustomProps>[] => [
  {
    headerCellProps: { sx: { width: 80 } },
    labelAccessor: () => <Trans i18nKey="internal-ai-prompts:col_version" />,
    accessor: ({ row }) => (
      <Typography variant="body2" fontWeight="bold">
        v{row.version}
      </Typography>
    ),
    sortAccessor: ({ row }) => row.version,
  },
  {
    headerCellProps: { sx: { width: 90 } },
    labelAccessor: () => <Trans i18nKey="internal-ai-prompts:col_status" />,
    accessor: ({ row, customTableProps }) =>
      customTableProps?.definition && row.version === customTableProps.definition.activeVersion ? (
        <Chip label={<Trans i18nKey="internal-ai-prompts:active" />} color="success" size="small" />
      ) : null,
  },
  {
    labelAccessor: () => <Trans i18nKey="internal-ai-prompts:col_full_text_preview" />,
    accessor: ({ row }) => (
      <Typography
        variant="body2"
        sx={{ fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 400 }}
      >
        {row.fullText.slice(0, 120)}
        {row.fullText.length > 120 ? '...' : ''}
      </Typography>
    ),
  },
  {
    headerCellProps: { sx: { width: 80 } },
    labelAccessor: () => <Trans i18nKey="internal-ai-prompts:col_months" />,
    accessor: ({ row, customTableProps }) =>
      customTableProps?.isText ? <Typography variant="body2">{(row as AiTextPromptVersionResponseDTO).months}</Typography> : null,
    sortAccessor: ({ row, customTableProps }) => (customTableProps?.isText ? (row as AiTextPromptVersionResponseDTO).months : 0),
    hidden: ({ customTableProps }) => !customTableProps?.isText,
  },
  {
    headerCellProps: { sx: { width: 100 } },
    labelAccessor: () => <Trans i18nKey="internal-ai-prompts:col_reasoning_level" />,
    accessor: ({ row, customTableProps }) =>
      customTableProps?.isText ? <Chip label={(row as AiTextPromptVersionResponseDTO).reasoningLevel} size="small" variant="outlined" /> : null,
    hidden: ({ customTableProps }) => !customTableProps?.isText,
  },
  {
    headerCellProps: { sx: { width: 140 } },
    labelAccessor: () => <Trans i18nKey="internal-ai-prompts:col_actions" />,
    accessor: ({ row, customTableProps }) => {
      const isActive = customTableProps?.definition && row.version === customTableProps.definition.activeVersion
      return (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title={<Trans i18nKey="internal-ai-prompts:edit_version" />}>
            <IconButton size="small" onClick={() => customTableProps?.onEdit(row)}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          {!isActive && (
            <Tooltip title={<Trans i18nKey="internal-ai-prompts:set_as_active" />}>
              <IconButton size="small" onClick={() => customTableProps?.onSetActive(row.version)} color="success">
                <Star fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {!isActive && (
            <Tooltip title={<Trans i18nKey="internal-ai-prompts:delete_version" />}>
              <IconButton size="small" onClick={() => customTableProps?.onDelete(row.version)} color="error">
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )
    },
  },
]

const VALID_PROMPT_TYPES: readonly PromptType[] = ['text', 'image']

export const AiPromptDetailPage: FC = () => {
  usePage(pageService.getInternalPageWithId('internal-ai-prompt-detail'))
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { type: rawType, name } = useRequiredParams<{ type: string; name: string }>()

  const isValidType = VALID_PROMPT_TYPES.includes(rawType as PromptType)
  const type: PromptType = isValidType ? (rawType as PromptType) : 'text'
  const isText = type === 'text'

  const textDefQuery = useGetTextDefinitionQuery(name, { skip: !isText || !isValidType })
  const imageDefQuery = useGetImageDefinitionQuery(name, { skip: isText || !isValidType })
  const definition = isText ? textDefQuery.data : imageDefQuery.data
  const isDefLoading = isText ? textDefQuery.isLoading : imageDefQuery.isLoading
  const isDefError = isText ? textDefQuery.isError : imageDefQuery.isError

  const textVersionsQuery = useGetTextVersionsQuery(name, { skip: !isText || !isValidType })
  const imageVersionsQuery = useGetImageVersionsQuery(name, { skip: isText || !isValidType })
  const versions: AiPromptVersion[] = useMemo(
    () => (isText ? textVersionsQuery.data : imageVersionsQuery.data) || [],
    [isText, textVersionsQuery.data, imageVersionsQuery.data]
  )
  const isVersionsLoading = isText ? textVersionsQuery.isLoading : imageVersionsQuery.isLoading

  const [createTextVersion, { isLoading: isCreatingTextVer }] = useCreateTextVersionMutation()
  const [createImageVersion, { isLoading: isCreatingImageVer }] = useCreateImageVersionMutation()
  const [updateTextVersion, { isLoading: isUpdatingTextVer }] = useUpdateTextVersionMutation()
  const [updateImageVersion, { isLoading: isUpdatingImageVer }] = useUpdateImageVersionMutation()
  const [deleteTextVersion] = useDeleteTextVersionMutation()
  const [deleteImageVersion] = useDeleteImageVersionMutation()
  const [updateTextDefinition] = useUpdateTextDefinitionMutation()
  const [updateImageDefinition] = useUpdateImageDefinitionMutation()

  const [versionDialogOpen, setVersionDialogOpen] = useState(false)
  const [versionDialogMode, setVersionDialogMode] = useState<'create' | 'edit'>('create')
  const [editingVersion, setEditingVersion] = useState<AiPromptVersion | null>(null)
  const [deleteConfirmVersion, setDeleteConfirmVersion] = useState<number | null>(null)

  const [columns, setColumns] = useState(() => buildVersionColumns())
  const handleColumnsUpdate = useCallback((updatedColumns: GRTableColumn<AiPromptVersion, VersionTableCustomProps>[]) => {
    setColumns(updatedColumns)
  }, [])

  const nextVersion = useMemo(() => {
    if (versions.length === 0) return 1
    return Math.max(...versions.map((v) => v.version)) + 1
  }, [versions])

  const handleOpenCreate = useCallback(() => {
    setEditingVersion(null)
    setVersionDialogMode('create')
    setVersionDialogOpen(true)
  }, [])

  const handleOpenEdit = useCallback((version: AiPromptVersion) => {
    setEditingVersion(version)
    setVersionDialogMode('edit')
    setVersionDialogOpen(true)
  }, [])

  const handleVersionSubmit = useCallback(
    async (values: VersionFormValues) => {
      try {
        if (versionDialogMode === 'create') {
          if (isText) {
            await createTextVersion({
              name,
              body: {
                version: values.version,
                fullText: values.fullText,
                months: values.months,
                reasoningLevel: values.reasoningLevel,
                setAsActive: values.setAsActive,
              },
            }).unwrap()
          } else {
            await createImageVersion({
              name,
              body: { version: values.version, fullText: values.fullText, setAsActive: values.setAsActive },
            }).unwrap()
          }
          dispatch(displaySnackBar({ message: t('internal-ai-prompts:version_created'), severity: 'success', open: true }))
        } else if (editingVersion) {
          if (isText) {
            await updateTextVersion({
              name,
              version: editingVersion.version,
              body: { fullText: values.fullText, months: values.months, reasoningLevel: values.reasoningLevel, setAsActive: values.setAsActive },
            }).unwrap()
          } else {
            await updateImageVersion({
              name,
              version: editingVersion.version,
              body: { fullText: values.fullText, setAsActive: values.setAsActive },
            }).unwrap()
          }
          dispatch(displaySnackBar({ message: t('internal-ai-prompts:version_updated'), severity: 'success', open: true }))
        }
        setVersionDialogOpen(false)
      } catch {
        const errorKey = versionDialogMode === 'create' ? 'version_create_error' : 'version_update_error'
        dispatch(displaySnackBar({ message: t(`internal-ai-prompts:${errorKey}`), severity: 'error', open: true }))
      }
    },
    [versionDialogMode, editingVersion, isText, name, createTextVersion, createImageVersion, updateTextVersion, updateImageVersion, dispatch, t]
  )

  const handleSetActive = useCallback(
    async (version: number) => {
      try {
        if (isText) {
          await updateTextDefinition({ name, body: { activeVersion: version } }).unwrap()
        } else {
          await updateImageDefinition({ name, body: { activeVersion: version } }).unwrap()
        }
        dispatch(displaySnackBar({ message: t('internal-ai-prompts:active_version_updated'), severity: 'success', open: true }))
      } catch {
        dispatch(displaySnackBar({ message: t('internal-ai-prompts:active_version_update_error'), severity: 'error', open: true }))
      }
    },
    [isText, name, updateTextDefinition, updateImageDefinition, dispatch, t]
  )

  const handleDelete = useCallback(
    async (version: number) => {
      try {
        if (isText) {
          await deleteTextVersion({ name, version }).unwrap()
        } else {
          await deleteImageVersion({ name, version }).unwrap()
        }
        dispatch(displaySnackBar({ message: t('internal-ai-prompts:version_deleted'), severity: 'success', open: true }))
      } catch {
        dispatch(displaySnackBar({ message: t('internal-ai-prompts:version_delete_error'), severity: 'error', open: true }))
      }
      setDeleteConfirmVersion(null)
    },
    [isText, name, deleteTextVersion, deleteImageVersion, dispatch, t]
  )

  const customTableProps: VersionTableCustomProps = useMemo(
    () => ({
      definition,
      isText,
      onEdit: handleOpenEdit,
      onSetActive: handleSetActive,
      onDelete: setDeleteConfirmVersion,
    }),
    [definition, isText, handleOpenEdit, handleSetActive, setDeleteConfirmVersion]
  )

  const editDefaults = useMemo((): Partial<VersionFormValues> | undefined => {
    if (!editingVersion) return undefined
    return {
      version: editingVersion.version,
      fullText: editingVersion.fullText,
      months: isTextVersion(editingVersion) ? editingVersion.months : 14,
      reasoningLevel: isTextVersion(editingVersion) ? editingVersion.reasoningLevel : 'medium',
      setAsActive: false,
    }
  }, [editingVersion])

  if (!isValidType || (!isDefLoading && !definition && !isDefError)) {
    return (
      <>
        <Button component={Link} to="/internal/ai-prompts" startIcon={<ArrowBack />} sx={{ mb: 2 }}>
          {t('internal-ai-prompts:back_to_prompts')}
        </Button>
        <Typography color="error">{t('internal-ai-prompts:definition_not_found')}</Typography>
      </>
    )
  }

  if (isDefError) {
    return (
      <>
        <Button component={Link} to="/internal/ai-prompts" startIcon={<ArrowBack />} sx={{ mb: 2 }}>
          {t('internal-ai-prompts:back_to_prompts')}
        </Button>
        <Typography color="error">{t('internal-ai-prompts:definition_load_error')}</Typography>
      </>
    )
  }

  if (isDefLoading) return <GRCircularProgress />

  return (
    <>
      <Button component={Link} to="/internal/ai-prompts" startIcon={<ArrowBack />} sx={{ mb: 2 }}>
        {t('internal-ai-prompts:back_to_prompts')}
      </Button>

      <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Grid item>
          <Typography variant="h5" fontWeight="bold">
            {t('internal-ai-prompts:detail_page_title', { name })}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5, alignItems: 'center' }}>
            <Chip label={t(`internal-ai-prompts:type_${type}`)} size="small" variant="outlined" />
            {definition && (
              <Typography variant="body2" color="text.secondary">
                {t('internal-ai-prompts:col_active_version')}: v{definition.activeVersion}
              </Typography>
            )}
          </Box>
        </Grid>
        <Grid item>
          <Button variant="contained" startIcon={<Add />} onClick={handleOpenCreate}>
            {t('internal-ai-prompts:add_version')}
          </Button>
        </Grid>
      </Grid>

      <TableContainer component={Card}>
        <GRTable
          columns={columns}
          onColumnsUpdated={handleColumnsUpdate}
          rows={versions}
          customProps={customTableProps}
          rowIdKey={(row) => row.id}
          isLoading={isVersionsLoading}
          striped
          gridlines
          noRowsLabel={t('internal-common:no_rows')}
        />
      </TableContainer>

      <VersionFormDialog
        open={versionDialogOpen}
        onClose={() => setVersionDialogOpen(false)}
        onSubmit={handleVersionSubmit}
        isLoading={isCreatingTextVer || isCreatingImageVer || isUpdatingTextVer || isUpdatingImageVer}
        promptType={type}
        mode={versionDialogMode}
        defaultValues={versionDialogMode === 'create' ? { version: nextVersion } : editDefaults}
      />

      <Dialog open={deleteConfirmVersion !== null} onClose={() => setDeleteConfirmVersion(null)}>
        <DialogTitle>{t('internal-ai-prompts:delete_confirm_title', { version: deleteConfirmVersion })}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('internal-ai-prompts:delete_confirm_message')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmVersion(null)}>{t('internal-ai-prompts:cancel')}</Button>
          <Button onClick={() => deleteConfirmVersion !== null && handleDelete(deleteConfirmVersion)} color="error" variant="contained">
            {t('internal-ai-prompts:delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
